import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAvailableCallerIds } from '@/api/agents'
import { invalidateVoiceToken, useVoiceToken } from '@/api/voice'
import { useAuthStore } from '@/stores/useAuthStore'
import { retenirFournisseurs } from '@/lib/voix/registre'
import {
  connecterTelnyx,
  deconnecterTelnyx,
  proposerJetonTelnyx,
  surDemandeDeJetonTelnyx,
  telnyxActif,
} from '@/lib/voix/telnyx'

/**
 * Lifecycle of the Telnyx phone, next to useTwilioDevice. Connects only when
 * the voice token carries a Telnyx phone, i.e. the company has a Telnyx number.
 *
 * Mount ONCE, in VoiceProvider.
 */
export function useTelnyxPhone() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated())
  const queryClient = useQueryClient()
  const tokenQuery = useVoiceToken(isAuthenticated)
  const { data: numeros } = useAvailableCallerIds()

  useEffect(() => {
    if (numeros) retenirFournisseurs(numeros)
  }, [numeros])

  const telnyx = tokenQuery.data?.telephones?.find((t) => t.fournisseur === 'telnyx')
  const jeton = telnyx?.token
  const identite = telnyx?.identite

  useEffect(() => {
    if (!jeton || !identite) return
    if (telnyxActif()) proposerJetonTelnyx(jeton, identite)
    else
      void connecterTelnyx(jeton, identite).catch((err) =>
        console.error('[useTelnyxPhone] connect failed:', err),
      )
  }, [jeton, identite])

  useEffect(() => {
    surDemandeDeJetonTelnyx(() => void invalidateVoiceToken(queryClient))
    return () => surDemandeDeJetonTelnyx(null)
  }, [queryClient])

  // Logout / leaving the dashboard. Only a real logout (tokens cleared) turns
  // the phone off: a session renewal after a nap used to, and nothing turned
  // it back on until the voice token changed (24 Oct 2026).
  useEffect(() => {
    return () => {
      if (!useAuthStore.getState().accessToken) deconnecterTelnyx()
    }
  }, [])
}
